import { HathorWalletServiceWallet, Network, config } from '@hathor/wallet-lib';
import type { GetHistoryObject, AddressInfoObject } from '@hathor/wallet-lib/lib/wallet/types';
import { NETWORKS, WALLET_SERVICE_URLS, WALLET_SERVICE_WS_URLS, TOKEN_IDS } from '../constants';
import type { WalletBalance, TransactionHistoryItem, TokenBalanceInfo } from '../types/wallet';
import { toBigInt } from '../utils/hathor';
import { createLogger } from '../utils/logger';
import { ERROR_PATTERNS } from '../errors/WalletConnectionErrors';

const log = createLogger('ReadOnlyWalletWrapper');

export interface AddressInfo {
  address: string;
  index: number;
  transactions: number;
}

/**
 * Service for managing a read-only wallet instance using wallet-lib.
 * This wallet can perform all read operations but cannot sign transactions.
 */
export class ReadOnlyWalletWrapper {
  private wallet: HathorWalletServiceWallet | null = null;
  private isInitializing = false;
  private currentNetwork: string | null = null;

  /**
   * Initialize the read-only wallet with the user's xpubkey.
   *
   * @param xpub - The extended public key (xpub) for the wallet
   * @param network - The network to connect to ('mainnet' or 'testnet')
   */
  async initialize(xpub: string, network: string = NETWORKS.MAINNET): Promise<void> {
    if (this.isInitializing) {
      throw new Error(ERROR_PATTERNS.ALREADY_INITIALIZING);
    }

    if (this.wallet?.isReady() && this.currentNetwork === network) {
      return;
    }

    // If wallet is ready but on a different network, stop it first
    if (this.wallet?.isReady() && this.currentNetwork !== network) {
      log.info(`Network mismatch: wallet on ${this.currentNetwork}, requested ${network}. Reinitializing.`);
      await this.wallet.stop();
      this.wallet = null;
      this.currentNetwork = null;
    }

    this.isInitializing = true;

    try {
      // Network URL Mapping:
      // - User-facing networks: 'mainnet', 'testnet'
      // - wallet-lib networks: only 'mainnet' or 'testnet'
      let walletServiceUrl: string;
      let walletServiceWsUrl: string;
      let actualNetwork: string;

      if (network === NETWORKS.TESTNET) {
        walletServiceUrl = WALLET_SERVICE_URLS.TESTNET;
        walletServiceWsUrl = WALLET_SERVICE_WS_URLS.TESTNET;
        actualNetwork = 'testnet';
      } else {
        // Default to mainnet for any unrecognized network
        walletServiceUrl = WALLET_SERVICE_URLS.MAINNET;
        walletServiceWsUrl = WALLET_SERVICE_WS_URLS.MAINNET;
        actualNetwork = 'mainnet';
      }

      // Set global wallet-lib config
      config.setWalletServiceBaseUrl(walletServiceUrl);
      config.setWalletServiceBaseWsUrl(walletServiceWsUrl);
      config.setNetwork(actualNetwork);

      // Create wallet instance with xpub only
      this.wallet = new HathorWalletServiceWallet({
        xpub,
        network: new Network(actualNetwork),
        requestPassword: async () => '', // Not used for read-only mode
        enableWs: true, // Enable WebSocket for real-time updates
      });

      // Start wallet in read-only mode
      try {
        await this.wallet.startReadOnly();
      } catch (error: unknown) {
        // Check if this is a "wallet already loaded" error (400 status)
        // The wallet-service returns 400 when wallet already exists
        const errorObj = error as { response?: { data?: { error?: string } } };
        if (errorObj?.response?.data?.error === ERROR_PATTERNS.WALLET_ALREADY_LOADED) {
          // Wallet already exists, continue
        } else {
          // For other errors, re-throw
          throw error;
        }
      }

      this.currentNetwork = network;
    } catch (error) {
      log.error('Failed to initialize read-only wallet:', error);
      this.wallet = null;
      this.currentNetwork = null;
      throw error;
    } finally {
      // Ensure flag is always reset
      this.isInitializing = false;
    }
  }

  /**
   * Ensures wallet-lib's global config singleton has the correct URLs for our current network.
   * wallet-lib reads baseURL from config.getWalletServiceBaseUrl() at request time (not at
   * wallet construction time), so if anything resets the global config between initialize()
   * and an API call, requests silently go to the wrong wallet-service.
   */
  private ensureConfigUrls(): void {
    if (!this.currentNetwork) return;

    const expectedUrl = this.currentNetwork === NETWORKS.TESTNET
      ? WALLET_SERVICE_URLS.TESTNET
      : WALLET_SERVICE_URLS.MAINNET;

    const expectedWsUrl = this.currentNetwork === NETWORKS.TESTNET
      ? WALLET_SERVICE_WS_URLS.TESTNET
      : WALLET_SERVICE_WS_URLS.MAINNET;

    const actualUrl = config.getWalletServiceBaseUrl();
    if (actualUrl !== expectedUrl) {
      log.error(`Config URL mismatch! Expected ${expectedUrl} but got ${actualUrl}. Fixing.`);
      config.setWalletServiceBaseUrl(expectedUrl);
      config.setWalletServiceBaseWsUrl(expectedWsUrl);
    }
  }

  /**
   * Check if the wallet is ready for operations
   */
  isReady(): boolean {
    return this.wallet?.isReady() ?? false;
  }

  /**
   * Get the wallet instance
   */
  getWallet(): HathorWalletServiceWallet | null {
    return this.wallet;
  }

  /**
   * Get balance for all tokens or specific tokens
   * Returns a Map for O(1) token lookups
   */
  async getBalance(tokenId?: string): Promise<Map<string, WalletBalance>> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    // Ensure wallet-lib global config points to the correct network URLs
    // wallet-lib's axios reads baseURL from config at request time, so if anything
    // resets the global config between initialize() and getBalance(), requests go
    // to the wrong wallet-service.
    this.ensureConfigUrls();

    try {
      const balance = await this.wallet.getBalance(tokenId ?? null);

      // Transform the wallet-lib balance format (array) to Map
      const balances = new Map<string, WalletBalance>();

      // wallet-lib returns an array of balance objects
      if (Array.isArray(balance)) {
        for (const item of balance) {
          balances.set(item.token.id, {
            token: item.token.id,
            available: item.balance.unlocked != null ? toBigInt(item.balance.unlocked) : 0n,
            locked: item.balance.locked != null ? toBigInt(item.balance.locked) : 0n,
          });
        }
      }

      return balances;
    } catch (error) {
      log.error('Failed to get balance:', error);
      throw error;
    }
  }

  /**
   * Get token balances with full token info (id, name, symbol).
   * Unlike getBalance(), this preserves the token name and symbol from the API response.
   * Used by token discovery to find unregistered tokens.
   *
   * @param tokenId - Optional token ID to fetch balance for a specific token.
   *                  If null/undefined, fetches all known token balances.
   */
  async getAllTokenBalances(tokenId?: string): Promise<TokenBalanceInfo[]> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    this.ensureConfigUrls();

    try {
      const balance = await this.wallet.getBalance(tokenId ?? null);
      const result: TokenBalanceInfo[] = [];

      if (Array.isArray(balance)) {
        for (const item of balance) {
          result.push({
            uid: item.token.id,
            name: item.token.name,
            symbol: item.token.symbol,
            version: item.token.version,
            balance: {
              available: item.balance.unlocked != null ? toBigInt(item.balance.unlocked) : 0n,
              locked: item.balance.locked != null ? toBigInt(item.balance.locked) : 0n,
            },
          });
        }
      }

      return result;
    } catch (error) {
      log.error('Failed to get all token balances:', error);
      throw error;
    }
  }

  /**
   * Get transaction history
   */
  async getTransactionHistory(count: number = 10, skip: number = 0, tokenId: string = TOKEN_IDS.HTR): Promise<TransactionHistoryItem[]> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    this.ensureConfigUrls();

    try {
      const history: GetHistoryObject[] = await this.wallet.getTxHistory({
        token_id: tokenId,
        count,
        skip,
      });

      // Transform GetHistoryObject[] to TransactionHistoryItem[]
      return history.map((item: GetHistoryObject) => ({
        tx_id: item.txId,
        timestamp: item.timestamp,
        balance: toBigInt(item.balance),
        is_voided: item.voided,
      }));
    } catch (error) {
      log.error('Failed to get transaction history:', error);
      throw error;
    }
  }

  /**
   * Get current address
   */
  getCurrentAddress(): AddressInfo | null {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    try {
      const addressInfo: AddressInfoObject = this.wallet.getCurrentAddress();
      return {
        address: addressInfo.address,
        index: addressInfo.index,
        transactions: 0, // AddressInfoObject doesn't have transactions field
      };
    } catch (error) {
      log.error('Failed to get current address:', error);
      throw error; // Re-throw instead of returning null
    }
  }

  /**
   * Get address at a specific index
   */
  async getAddressAtIndex(index: number): Promise<AddressInfo | null> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    try {
      const addressStr: string = await this.wallet.getAddressAtIndex(index);
      return {
        address: addressStr,
        index: index,
        transactions: 0,
      };
    } catch (error) {
      log.error('Failed to get address at index:', error);
      throw error;
    }
  }

  /**
   * Get all addresses (returns an async generator)
   */
  async *getAllAddresses(): AsyncGenerator<AddressInfo> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    try {
      const addresses = this.wallet.getAllAddresses();
      for await (const addr of addresses) {
        yield {
          address: addr.address,
          index: addr.index,
          transactions: addr.transactions,
        };
      }
    } catch (error) {
      log.error('Failed to get all addresses:', error);
      throw error;
    }
  }

  /**
   * Check if an address belongs to this wallet
   */
  async isAddressMine(address: string): Promise<boolean> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    try {
      return await this.wallet.isAddressMine(address);
    } catch (error) {
      log.error('Failed to check if address is mine:', error);
      // Re-throw instead of returning false - calling code needs to distinguish
      // between "not mine" (false) and "failed to check" (error)
      throw error;
    }
  }

  /**
   * Get all token UIDs that the wallet has interacted with.
   * Calls wallet-service GET /wallet/tokens.
   */
  async getTokens(): Promise<string[]> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    this.ensureConfigUrls();

    try {
      const tokens = await this.wallet.getTokens();
      return tokens as string[];
    } catch (error) {
      log.error('Failed to get tokens:', error);
      throw error;
    }
  }

  /**
   * Check if the wallet has transactions outside the first address (index 0).
   * This is used to determine if the user can safely switch to single address mode.
   *
   * @returns Promise<boolean> - true if there are transactions on addresses with index > 0
   */
  async hasTxOutsideFirstAddress(): Promise<boolean> {
    if (!this.wallet) {
      throw new Error('Wallet not initialized');
    }

    try {
      return await this.wallet.hasTxOutsideFirstAddress();
    } catch (error) {
      log.error('Failed to check transactions outside first address:', error);
      throw error;
    }
  }

  /**
   * Stop the wallet and cleanup resources
   */
  async stop(): Promise<void> {
    if (!this.wallet) return;

    try {
      await this.wallet.stop();
    } finally {
      this.wallet = null;
      this.currentNetwork = null;
    }
  }

  /**
   * Register event listener for wallet events
   */
  on(event: string, callback: (data: unknown) => void): void {
    if (this.wallet) {
      this.wallet.on(event, callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event: string, callback: (data: unknown) => void): void {
    if (this.wallet) {
      this.wallet.off(event, callback);
    }
  }

  /**
   * Remove all event listeners from the wallet
   */
  removeAllListeners(): void {
    if (this.wallet) {
      this.wallet.removeAllListeners();
    }
  }
}

// Export a singleton instance
export const readOnlyWalletWrapper = new ReadOnlyWalletWrapper();
